import { useLocation } from 'react-router-dom';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { IUser } from '../../types/auth.types';
import {
  getUserDisplayName,
  getUserInitials,
} from '../../utils/userHelpers';
import {
  buildUserNavMenuConfig,
  PERSONA_ACCENT_STYLES,
} from '../../config/userNavMenu';

type MobileAccountIdentityProps = {
  user: IUser;
  /** Tappable strip at bottom of hamburger menu vs hero header in account sheet. */
  variant: 'teaser' | 'hero';
  badgeCount?: number;
  onOpen?: () => void;
  className?: string;
};

export function MobileAccountIdentity({
  user,
  variant,
  badgeCount = 0,
  onOpen,
  className = '',
}: MobileAccountIdentityProps) {
  const { pathname } = useLocation();
  const menu = buildUserNavMenuConfig(user, pathname);
  const accent = PERSONA_ACCENT_STYLES[menu.accent];
  const isTeaser = variant === 'teaser';

  const content = (
    <div
      className={`relative overflow-hidden rounded-2xl ${accent.mobileHero} ${
        isTeaser ? 'mx-0' : 'mx-4 mb-1 rounded-b-none rounded-t-2xl'
      } ${className}`}
    >
      <div
        aria-hidden
        className={`pointer-events-none absolute -left-6 -top-8 h-32 w-32 rounded-full blur-2xl ${accent.mobileHeroOrb}`}
      />
      <div
        aria-hidden
        className={`pointer-events-none absolute -bottom-10 -right-6 h-36 w-36 rounded-full blur-3xl ${accent.mobileHeroOrbSecondary}`}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_20%_0%,rgba(255,255,255,0.14),transparent_45%)]"
      />

      <div
        className={`relative flex items-center gap-3.5 ${
          isTeaser ? 'p-3.5' : 'px-4 pb-5 pt-7'
        }`}
      >
        <div className="relative shrink-0">
          <div
            className={`flex items-center justify-center rounded-full font-semibold text-white ${accent.mobileAvatar} ${
              isTeaser ? 'h-12 w-12 text-sm' : 'h-14 w-14 text-base'
            }`}
          >
            {getUserInitials(user)}
          </div>
          {badgeCount > 0 ? (
            <span className="absolute -right-0.5 -top-0.5 flex min-h-[1.125rem] min-w-[1.125rem] items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold leading-none text-white ring-2 ring-white/90">
              {badgeCount > 99 ? '99+' : badgeCount}
            </span>
          ) : null}
        </div>

        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-white/55">
            {menu.greeting}
          </p>
          <p
            className={`truncate font-semibold text-white ${
              isTeaser ? 'text-base' : 'text-lg'
            }`}
          >
            {getUserDisplayName(user)}
          </p>
          <p className="mt-0.5 truncate text-xs text-white/70">{user.email}</p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span
              className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${accent.mobileBadge}`}
            >
              {menu.personaLabel}
            </span>
            <span className="truncate text-[11px] text-white/60">
              {menu.hubLabel}
            </span>
          </div>
        </div>

        {isTeaser ? (
          <span
            className={`inline-flex shrink-0 items-center gap-0.5 rounded-full px-2.5 py-1.5 text-[11px] font-semibold transition ${accent.mobileCta}`}
          >
            Open
            <ChevronRightIcon className="h-3.5 w-3.5" />
          </span>
        ) : null}
      </div>
    </div>
  );

  if (isTeaser && onOpen) {
    return (
      <button
        type="button"
        onClick={onOpen}
        className="w-full text-left transition active:scale-[0.99]"
        aria-label={`Open ${menu.personaLabel} account menu for ${getUserDisplayName(user)}`}
      >
        {content}
      </button>
    );
  }

  return content;
}
